// Global Instructions Rule Applied!

import { useCallback, useEffect, useMemo, useState } from 'react'
import { getAccessToken } from '../../../core/auth/authService'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { store } from '../../../state_manager/store.ts'
import { fetchRunDescriptionWarnings } from '../services/investorStatementsService.ts'
import type {
  InvestorStatementRun,
  RunDescriptionWarnings
} from '../../../state_manager/data_models/investor_statement_run.ts'

const NONE: RunDescriptionWarnings = { statements: {}, account_documents: {} }

/**
 * useRunDescriptionWarnings — the agent-written company short descriptions
 * the run's stored fund reports and performance decks printed that still
 * need a person (not signed off, or changed since), per document id.
 *
 * Its own read, like Out of date, so the run read keeps its shape: asked
 * once the run has loaded, again whenever a document of the run is
 * generated (the documents' generated-at times change), and on `refetch`
 * (the form calls it when the user comes back to the tab - a sign-off on
 * the company form clears a warning).
 */
export function useRunDescriptionWarnings(run: InvestorStatementRun | null) {
  const [warnings, setWarnings] = useState<RunDescriptionWarnings>(NONE)
  const runId = run?.run.investor_statement_runs__id ?? null

  // what is stored, and when it was printed
  const printed = useMemo(
    () =>
      run
        ? [
            ...run.statements.map((s) => `${s.investor_statements__id}:${s.investor_statements__generated_at ?? ''}`),
            ...run.account_documents.map(
              (d) =>
                `${d.investor_statement_account_documents__id}:${d.investor_statement_account_documents__generated_at ?? ''}`
            )
          ].join('|')
        : '',
    [run]
  )

  const refetch = useCallback(async () => {
    if (!runId) return
    const res = await fetchRunDescriptionWarnings(runId, getAccessToken(store.getState().auth))
    if (res.error || !res.data) {
      showErrorToast(res.error || 'The description warnings of the run could not be read.', {
        key: `run-description-warnings-${runId}`
      })
      return
    }
    setWarnings(res.data)
  }, [runId])

  useEffect(() => {
    if (printed) void refetch()
  }, [printed, refetch])

  return { warnings, refetch }
}
